import { cn, formatPrice } from "@/shared/lib/utils";
import * as React from "react";
import TransparentButton from "../buttons/TransparentButton";
import Typography from "../Typography";

import type { ProjectType } from "@/entities/projects/model/types";
import { NavLink } from "react-router-dom";
import ArrowButton from "../buttons/ArrowButton";

export interface IProjectCardProps {
    className?: string;
    project: ProjectType;
    isActive?: boolean;
}

const ProjectCard = React.forwardRef<HTMLDivElement, IProjectCardProps>(
    ({ className, project, isActive }, ref) => {
        const [isHover, setIsHover] = React.useState<boolean>(false);
        const cardRef = React.useRef<HTMLDivElement>(null);

        // Мемоизация вычисляемых значений
        const building = React.useMemo(
            () =>
                `${new Date(project.startDate).getFullYear()} - ${new Date(
                    project.completionDate
                ).getFullYear()}`,
            [project.startDate, project.completionDate]
        );

        const imageUrl = React.useMemo(
            () => project.imageUrl || "/images/backgrounds/card.png",
            [project.imageUrl]
        );

        const formattedPrice = React.useMemo(
            () => formatPrice(project.priceFrom),
            [project.priceFrom]
        );

        // Оптимизация обработчиков событий
        React.useEffect(() => {
            const cardEl = cardRef.current;
            if (!cardEl) return;

            const isMobile = window.matchMedia(
                "(hover: none) and (pointer: coarse)"
            ).matches;

            const handleEnter = () => setIsHover(true);
            const handleLeave = () => setIsHover(false);

            const enterEvent = isMobile ? "touchstart" : "mouseenter";
            const leaveEvent = isMobile ? "touchend" : "mouseleave";

            cardEl.addEventListener(enterEvent, handleEnter);
            cardEl.addEventListener(leaveEvent, handleLeave);

            return () => {
                cardEl.removeEventListener(enterEvent, handleEnter);
                cardEl.removeEventListener(leaveEvent, handleLeave);
            };
        }, [ref]);

        return (
            <div
                ref={cardRef}
                className={cn(
                    "relative min-w-[300px] h-[300px] md:min-w-[400px] md:h-[400px] overflow-hidden",
                    className
                )}
            >
                <img
                    className={cn(
                        "absolute z-0 w-full h-full object-cover object-center transition-transform duration-300 ease-in-out",
                        (isHover || isActive) && "scale-[1.15]"
                    )}
                    src={imageUrl}
                    alt={`Новостройки ${building}`}
                    loading="lazy"
                />

                <div
                    className="relative w-full h-full p-[10px] md:p-[20px] pb-[20px] md:pb-[40px] flex flex-col justify-between"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)",
                    }}
                >
                    <div className="flex flex-row gap-[8px]">
                        <TransparentButton className="bg-white-50 font-[400] text-[10px] md:text-[14px] p-[10px] cursor-default">
                            {project.spec.objectCount} объекта
                        </TransparentButton>
                        <TransparentButton className="bg-white-50 font-[400] text-[10px] md:text-[14px] p-[10px] cursor-default">
                            От {formattedPrice}
                        </TransparentButton>
                        <TransparentButton className="bg-white-50 font-[400] text-[10px] md:text-[14px] p-[10px] cursor-default">
                            От {project.spec.areaFrom} кв м
                        </TransparentButton>
                    </div>

                    <div className="flex flex-row justify-between items-center">
                        <NavLink to={`/project/${project.id}`}>
                            <ArrowButton />
                        </NavLink>
                        <div className="flex flex-col items-end gap-[8px]">
                            <Typography className="text-white text-[18px] md:text-[20px] font-semibold md:font-bold">
                                Новостройки {building}
                            </Typography>
                            {isHover && (
                                <Typography className="text-white text-[12px] md:text-[16px] font-medium">
                                    Новостройки {building}
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

ProjectCard.displayName = "ProjectCard";

export default ProjectCard;